// PrivacyPolicy.js
import React, { useState } from 'react';
import { Shield, Lock, Eye, Database, Users, FileText, AlertCircle, ChevronDown, ChevronUp, Download, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const lastUpdated = 'January 15, 2025';
  const effectiveDate = 'January 1, 2025';

  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: Database,
      content: {
        intro: 'MediGo collects information to provide and improve our healthcare services. We collect:',
        items: [
          {
            title: 'Personal Information',
            details: 'Name, email address, phone number, date of birth, gender, national ID, and address when you register on our platform.'
          },
          {
            title: 'Medical Information',
            details: 'Medical history, current medications, allergies, test results, prescriptions, consultation notes, and other health-related information necessary for providing healthcare services.'
          },
          {
            title: 'Payment Information',
            details: 'Payment card details, M-Pesa information, insurance details, and billing addresses for processing payments.'
          },
          {
            title: 'Usage Information',
            details: 'Information about how you interact with our platform, including IP addresses, browser type, device information, and pages visited.'
          },
          {
            title: 'Location Information',
            details: 'Your location data when you use emergency services or search for nearby healthcare providers.'
          }
        ]
      }
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: Eye,
      content: {
        intro: 'We use your information to:',
        items: [
          {
            title: 'Provide Healthcare Services',
            details: 'Facilitate consultations, book appointments, process medicine orders, and coordinate emergency services.'
          },
          {
            title: 'Maintain Medical Records',
            details: 'Create and maintain your electronic health records for continuity of care.'
          },
          {
            title: 'Process Payments',
            details: 'Handle transactions for services, process insurance claims, and manage billing.'
          },
          {
            title: 'Communicate',
            details: 'Send appointment reminders, service updates, emergency alerts, and respond to inquiries.'
          },
          {
            title: 'Improve Services',
            details: 'Analyze usage patterns to enhance our platform and develop new features.'
          },
          {
            title: 'Legal Compliance',
            details: 'Comply with healthcare regulations, legal obligations, and respond to lawful requests.'
          }
        ]
      }
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing & Disclosure',
      icon: Users,
      content: {
        intro: 'We share your information only in the following circumstances:',
        items: [
          {
            title: 'Healthcare Providers',
            details: 'With doctors, hospitals, and pharmacies you choose to consult or receive services from.'
          },
          {
            title: 'Emergency Services',
            details: 'With emergency responders and hospitals when you request emergency assistance.'
          },
          {
            title: 'Insurance Providers',
            details: 'With your insurance company for claim processing, with your consent.'
          },
          {
            title: 'Service Providers',
            details: 'With trusted third-party services that help us operate our platform (payment processors, cloud storage).'
          },
          {
            title: 'Legal Requirements',
            details: 'When required by law, court order, or to protect rights, property, or safety.'
          },
          {
            title: 'With Your Consent',
            details: 'With other parties when you explicitly consent to such sharing.'
          }
        ]
      }
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: Lock,
      content: {
        intro: 'We implement robust security measures to protect your information:',
        items: [
          {
            title: 'Encryption',
            details: 'All data is encrypted in transit using SSL/TLS and at rest using AES-256 encryption.'
          },
          {
            title: 'Access Controls',
            details: 'Strict access controls and authentication mechanisms to prevent unauthorized access.'
          },
          {
            title: 'Regular Audits',
            details: 'Regular security audits and penetration testing to identify and fix vulnerabilities.'
          },
          {
            title: 'Compliance',
            details: 'Compliance with healthcare data protection standards and regulations.'
          },
          {
            title: 'Employee Training',
            details: 'Regular training for our staff on data protection and privacy practices.'
          },
          {
            title: 'Incident Response',
            details: 'Comprehensive incident response plan for potential data breaches.'
          }
        ]
      }
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      icon: Shield,
      content: {
        intro: 'You have the following rights regarding your personal information:',
        items: [
          {
            title: 'Access',
            details: 'Request access to your personal and medical information we hold.'
          },
          {
            title: 'Correction',
            details: 'Request correction of inaccurate or incomplete information.'
          },
          {
            title: 'Deletion',
            details: 'Request deletion of your account and associated data, subject to legal retention requirements.'
          },
          {
            title: 'Portability',
            details: 'Request a copy of your medical records in a portable format.'
          },
          {
            title: 'Consent Withdrawal',
            details: 'Withdraw consent for data processing where consent is the legal basis.'
          },
          {
            title: 'Complaint',
            details: 'Lodge a complaint with data protection authorities.'
          }
        ]
      }
    },
    {
      id: 'cookies',
      title: 'Cookies & Tracking',
      icon: FileText,
      content: {
        intro: 'We use cookies and similar technologies to:',
        items: [
          {
            title: 'Essential Cookies',
            details: 'Enable core functionality like security, network management, and accessibility.'
          },
          {
            title: 'Analytics Cookies',
            details: 'Understand how users interact with our platform to improve services.'
          },
          {
            title: 'Preference Cookies',
            details: 'Remember your settings and preferences for future visits.'
          },
          {
            title: 'Marketing Cookies',
            details: 'Deliver relevant advertisements and measure their effectiveness (with consent).'
          }
        ]
      }
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-12 h-12 mr-3" />
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-center text-primary-100 max-w-3xl mx-auto">
            Your privacy is fundamental to us. This policy explains how MediGo collects, uses, and protects your information.
          </p>
          <div className="text-center mt-4 text-primary-200">
            <p>Effective Date: {effectiveDate} | Last Updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-8 px-4 -mt-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-primary-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold mb-2 text-secondary-800">Key Points</h2>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary-600 mr-2">•</span>
                    We collect only necessary information to provide healthcare services
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 mr-2">•</span>
                    Your medical information is shared only with healthcare providers you choose
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 mr-2">•</span>
                    All data is encrypted and secured following healthcare industry standards
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 mr-2">•</span>
                    You have full control over your data and can request access, correction, or deletion
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 mr-2">•</span>
                    We never sell your personal or medical information to third parties
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {sections.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSection === section.id;
              
              return (
                <div key={section.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                        <Icon className="w-5 h-5 text-primary-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-secondary-800">{section.title}</h3>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-700 mb-4">{section.content.intro}</p>
                      <div className="space-y-3">
                        {section.content.items.map((item, index) => (
                          <div key={index} className="border-l-2 border-primary-200 pl-4">
                            <h4 className="font-semibold text-secondary-800 mb-1">{item.title}</h4>
                            <p className="text-gray-600 text-sm">{item.details}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Additional Sections */}
          <div className="mt-8 space-y-6">
            {/* Children's Privacy */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-3 text-secondary-800">Children's Privacy</h3>
              <p className="text-gray-600">
                MediGo does not knowingly collect personal information from children under 13 without parental consent. 
                For minors under 18, parental or guardian consent is required to use our services. If we become aware 
                that we have collected personal information from a child under 13 without verification of parental consent, 
                we will delete that information.
              </p>
            </div>

            {/* International Data Transfers */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-3 text-secondary-800">International Data Transfers</h3>
              <p className="text-gray-600">
                Your information may be transferred to and maintained on servers located outside of Kenya. 
                We ensure that such transfers comply with applicable data protection laws and that your 
                information remains protected to the standards described in this privacy policy.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-3 text-secondary-800">Changes to This Privacy Policy</h3>
              <p className="text-gray-600">
                We may update this privacy policy from time to time to reflect changes in our practices or 
                legal requirements. We will notify you of any material changes via email or through a 
                prominent notice on our platform. Your continued use of MediGo after such modifications 
                constitutes your acceptance of the updated privacy policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
              <h3 className="text-lg font-semibold mb-3">Contact Our Privacy Team</h3>
              <p className="text-primary-100 mb-4">
                If you have any questions, concerns, or requests regarding this privacy policy or our data practices, 
                please contact us:
              </p>
              <div className="space-y-2">
                <p className="flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  Email: privacy@medigo.co.ke
                </p>
                <p className="flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  Data Protection Officer: dpo@medigo.co.ke
                </p>
                <p>
                  Address: MediGo Privacy Team, Westlands, Nairobi, Kenya
                </p>
              </div>
            </div>

            {/* Download Policy */}
            <div className="text-center">
              <button className="inline-flex items-center bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition">
                <Download className="w-5 h-5 mr-2" />
                Download Privacy Policy (PDF)
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;